import { initializeApp, getApps, getApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth } from 'firebase/auth';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  getToken,
  AppCheck,
} from 'firebase/app-check';
import { recaptchaEnterpriseSiteKey, appCheckDebugToken } from './env';

const firebaseConfig = {
  apiKey: "AIzaSyARBamBuZ3fnphXDH4UsXR5J_cC7Cw9KQA",
  authDomain: "lnb-staging-483105.firebaseapp.com",
  projectId: "lnb-staging-483105",
  storageBucket: "lnb-staging-483105.firebasestorage.app",
  messagingSenderId: "315639738240",
  appId: "1:315639738240:web:67efde0d7d6228d92cdb65",
  measurementId: "G-DP2GRDKDRE"
};

const APP_CHECK_HMR_KEY = '__lnbWebAppCheck';

type AppCheckHmrStore = {
  instance: AppCheck;
  siteKey: string;
};

const getHmrStore = (): AppCheckHmrStore | undefined => {
  if (typeof globalThis === 'undefined') {
    return undefined;
  }
  return (globalThis as unknown as Record<string, AppCheckHmrStore | undefined>)[APP_CHECK_HMR_KEY];
};

const setHmrStore = (store: AppCheckHmrStore) => {
  (globalThis as unknown as Record<string, AppCheckHmrStore | undefined>)[APP_CHECK_HMR_KEY] = store;
};

const app: FirebaseApp = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
const auth: Auth = getAuth(app);

let appCheck: AppCheck | null = null;
let appCheckInitAttempted = false;
let appCheckTokenAcquired = false;
let appCheckReady: Promise<AppCheck | null> | null = null;

/**
 * Debug tokens must be assigned on `self` before initializeAppCheck().
 * Use EXPO_PUBLIC_APPCHECK_DEBUG_TOKEN=true to generate one, or a registered UUID.
 */
const applyAppCheckDebugToken = () => {
  if (typeof self === 'undefined' || !appCheckDebugToken) {
    return;
  }

  (self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean | string }).FIREBASE_APPCHECK_DEBUG_TOKEN =
    appCheckDebugToken.toLowerCase() === 'true' ? true : appCheckDebugToken;
};

/**
 * Initialize App Check once for the web Firebase app.
 * Native Android/iOS never load this module (.web.ts).
 * HMR reuses the previous instance so React Fast Refresh cannot double-init.
 */
const initializeWebAppCheck = (): AppCheck | null => {
  if (appCheck) {
    return appCheck;
  }
  if (typeof window === 'undefined') {
    return null;
  }

  const existing = recaptchaEnterpriseSiteKey ? getHmrStore() : undefined;
  if (existing && existing.siteKey === recaptchaEnterpriseSiteKey) {
    appCheck = existing.instance;
    appCheckInitAttempted = true;
    return appCheck;
  }

  if (appCheckInitAttempted) {
    return appCheck;
  }
  appCheckInitAttempted = true;

  if (!recaptchaEnterpriseSiteKey) {
    console.warn(
      '[AppCheck] EXPO_PUBLIC_RECAPTCHA_ENTERPRISE_SITE_KEY is not set. ' +
        'App Check was not initialized. Phone OTP will fail with auth/invalid-app-credential ' +
        'if Authentication App Check enforcement is on.'
    );
    return null;
  }

  applyAppCheckDebugToken();

  try {
    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(recaptchaEnterpriseSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
    setHmrStore({ instance: appCheck, siteKey: recaptchaEnterpriseSiteKey });
    console.log('[AppCheck] Initialized (ReCaptchaEnterpriseProvider, auto-refresh on)');
  } catch (error) {
    console.error('[AppCheck] initializeAppCheck failed:', error);
    const reused = getHmrStore();
    if (reused) {
      appCheck = reused.instance;
      return appCheck;
    }
    appCheck = null;
    return null;
  }

  return appCheck;
};

export type AppCheckDebugState = {
  initialized: boolean;
  tokenAcquired: boolean;
  projectId: string;
  authDomain: string;
  appId: string;
};

export const getAppCheckDebugState = (): AppCheckDebugState => ({
  initialized: appCheck !== null,
  tokenAcquired: appCheckTokenAcquired,
  projectId: firebaseConfig.projectId,
  authDomain: firebaseConfig.authDomain,
  appId: firebaseConfig.appId,
});

/**
 * Resolve after App Check has a token (or after a failed attempt).
 * Call before Firebase Auth operations so requests can attach the header.
 */
export const ensureAppCheckReady = (): Promise<AppCheck | null> => {
  if (!appCheckReady) {
    appCheckReady = (async () => {
      const instance = initializeWebAppCheck();
      if (!instance) {
        console.log('[AppCheck] Token acquired: NO');
        appCheckTokenAcquired = false;
        return null;
      }

      try {
        const tokenResult = await getToken(instance, false);
        appCheckTokenAcquired = Boolean(tokenResult?.token);
        // Never log the token itself.
        console.log('[AppCheck] Token acquired:', appCheckTokenAcquired ? 'YES' : 'NO');
      } catch (error) {
        appCheckTokenAcquired = false;
        console.log('[AppCheck] Token acquired: NO');
        console.error('[AppCheck] Failed to obtain App Check token:', error);
      }

      return instance;
    })();
  }

  return appCheckReady;
};

initializeWebAppCheck();

export { auth, app, appCheck };
